import { useEffect } from "react";
import { isMfeDataChangedMessage } from "@webcore/services/mfe/mfeMessaging.ts";
import { useDataChangeInvalidator } from "@webcore/hooks/useDataChangeInvalidator.ts";

/**
 * Invalidates the MFE's own RTK Query cache when the parent app forwards a SignalR data-change
 * notification. The MFE also runs its own SignalR connection (MfeRealtimeSync), but inside the
 * iframe that connection is best-effort and can silently fail to establish — when it does, the
 * rendered widget/view code (served from the iframe's cached getDashboard result) goes stale until
 * a full page reload. The forwarded channel from the parent's app-root connection is a reliable
 * backup so the view refreshes in place.
 *
 * Mounted inside the store Provider but outside MfeBootstrap's "ready" gate so it always listens.
 * Renders nothing.
 */
function MfeDataChangeListener() {
  const invalidate = useDataChangeInvalidator();

  useEffect(() => {
    const window_message = (event: MessageEvent) => {
      // Accept notifications only from the parent window. They carry no token; the container name
      // just drives local cache invalidation, and reads remain gated by the server-side session.
      if (event.source !== window.parent || !isMfeDataChangedMessage(event.data)) {
        return;
      }

      invalidate(event.data.containerName, event.data.sourceClientInstanceId);
    };

    window.addEventListener("message", window_message);
    return () => window.removeEventListener("message", window_message);
  }, [invalidate]);

  return null;
}

export default MfeDataChangeListener;
